<template>
	<div :class="['status-message', `is-${type}`]" role="group">
		<component :is="icon" size="lg" />

		<div class="status-message-content">
			<slot />

			<TAction
				v-if="link"
				class="status-message-link"
				appearance="link"
				:to="link.to"
			>
				{{ link.text }}
				<ArrowRightIcon />
			</TAction>
		</div>

		<button
			v-if="closeable"
			class="status-message-close"
			type="button"
			:aria-label="$t('global-status-message-close')"
			@click="emit('close')"
		>
			<CloseIcon />
		</button>
	</div>
</template>

<script setup lang="ts">
import { type Component, computed } from 'vue'
import { type RouteLocationRaw } from 'vue-router'

import TAction from '@/app/common/TAction.vue'
import ArrowRightIcon from '@/shared/icons/ArrowRightIcon.vue'
import CheckCircleIcon from '@/shared/icons/CheckCircleIcon.vue'
import CloseIcon from '@/shared/icons/CloseIcon.vue'
import InfoCircleIcon from '@/shared/icons/InfoCircleIcon.vue'
import WarningCircleIcon from '@/shared/icons/WarningCircleIcon.vue'
import WarningTriangleIcon from '@/shared/icons/WarningTriangleIcon.vue'

export interface Props {
	type?: 'info' | 'success' | 'warning' | 'alert'
	link?: {
		text: string
		to: RouteLocationRaw
	}
	closeable?: boolean
}

const { type = 'info' } = defineProps<Props>()

const emit = defineEmits<{
	close: []
}>()

const iconMap: Record<Exclude<Props['type'], undefined>, Component> = {
	info: InfoCircleIcon,
	success: CheckCircleIcon,
	warning: WarningCircleIcon,
	alert: WarningTriangleIcon,
}

const icon = computed(() => iconMap[type])
</script>

<style scoped>
.status-message {
	--status-message-border-size: var(--size-2);

	align-items: start;
	border: var(--status-message-border-size) solid currentcolor;
	border-radius: var(--border-radius-md);
	display: flex;
	gap: var(--size-12);
	inline-size: fit-content;
	padding: calc(var(--size-16) - var(--status-message-border-size));
	padding-inline-end: var(--size-16);

	&.is-info {
		background: var(--c-status-info-light);
		color: var(--c-status-info-dark);
	}

	&.is-success {
		background: var(--c-status-success-light);
		color: var(--c-status-success-dark);
	}

	&.is-warning {
		background: var(--c-status-warning-light);
		color: var(--c-status-warning-dark);
	}

	&.is-alert {
		background: var(--c-status-alert-light);
		color: var(--c-status-alert-dark);
	}

	:deep(a) {
		color: currentcolor;
	}
}

.status-message-content {
	flex-grow: 1;
}

.status-message-link {
	margin-block-start: var(--size-10);
}

.status-message-close {
	border-radius: var(--border-radius-md);
	display: flex;
}
</style>
